import { useState, type ReactNode } from 'react'
import StudioMark from './StudioMark'
import { Check, Circle, Eye, EyeOff } from 'lucide-react'
import { MIN_PASSWORD_LENGTH, type PasswordProblem } from '../api/auth'
import BuildCredit from './BuildCredit'

/**
 * The shell shared by sign-in and accept-invite.
 *
 * Deliberately without the app header: there is no calendar to navigate to yet,
 * and offering links that bounce back to this page is a small lie.
 *
 * <b>It carries the build credit</b> for four of the five screens that reach the
 * app without a session — sign-in, accept-invite, forgot-password and
 * reset-password — including the states each of them returns early into. Placed
 * here rather than in each view for the reason the component itself exists: one
 * home means one thing to change, and none to forget (D-228, amended for the
 * no-session screens on 2026-08-20).
 */
export default function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div className="flex min-h-full flex-col">
      {/* The card keeps its own centring inside a `flex-1` row, so adding the
          credit below moved nothing that was already on the screen. */}
      <div className="flex flex-1 items-center justify-center px-5 py-12">
        <main className="w-full max-w-[26rem]">
          <div className="mb-7 flex items-center gap-3">
            <StudioMark size="lg" />
            <div>
              <p className="text-[11px] font-bold tracking-[0.14em] text-ink-3 uppercase">
                Cabot Dance Academy
              </p>
              <p className="text-[14px] font-semibold">Studio System</p>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-surface p-7 shadow-sm">
            <h1 className="font-display text-2xl">{title}</h1>
            <p className="mt-1.5 mb-6 text-[13.5px] leading-relaxed text-ink-3">{subtitle}</p>
            {children}
          </div>

          {footer && <div className="mt-5 text-center text-[12.5px] text-ink-3">{footer}</div>}
        </main>
      </div>

      <BuildCredit tone="quiet" />
    </div>
  )
}

export function FormError({ title, detail }: { title: string; detail?: string }) {
  return (
    // role="alert" so a screen reader announces it. A sighted user sees the red
    // box appear; without this the failure is silent to everyone else.
    <div
      role="alert"
      className="mb-5 rounded-lg border border-crit bg-crit-bg px-3.5 py-3 text-[13px] text-crit"
    >
      <p className="font-semibold">{title}</p>
      {detail && <p className="mt-1 leading-relaxed">{detail}</p>}
    </div>
  )
}

export function Field({
  label,
  hint,
  invalid,
  ...props
}: {
  label: string
  hint?: string
  /** Draws the box in the critical colour and announces the hint as an alert. */
  invalid?: boolean
} & React.InputHTMLAttributes<HTMLInputElement>) {
  const [revealed, setRevealed] = useState(false)

  /*
   * A password can be shown, because the alternative is worse (D-267).
   *
   * Typing a password blind on a phone keyboard is where wrong ones come from,
   * and the recourse — "your password is incorrect", try again — costs far more
   * than the glance. It is off by default and reverts on its own; the risk it
   * trades against is somebody reading over a shoulder, which the person
   * holding the phone is the right one to judge.
   *
   * <b>The type is swapped rather than the field replaced</b>, so the browser's
   * own password manager keeps hold of it — a second input would break autofill
   * and, on iOS, the strong-password suggestion.
   */
  const isPassword = props.type === 'password'
  const type = isPassword && revealed ? 'text' : props.type

  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">{label}</span>

      <span className="relative block">
        <input
          {...props}
          type={type}
          aria-invalid={invalid || undefined}
          className={`w-full rounded-lg border bg-surface-2 py-2.5 text-[14px] text-ink transition-colors outline-none placeholder:text-ink-3 focus:bg-surface ${
            // Room for the button, so a long password does not run underneath it.
            isPassword ? 'pr-11 pl-3' : 'px-3'
          } ${invalid ? 'border-crit focus:border-crit' : 'border-line-2 focus:border-accent'}`}
        />

        {isPassword && (
          /*
           * Outside the tab order deliberately. Somebody filling this in wants
           * the next field, not a control they did not ask for — and a keyboard
           * user reaching it by accident between password and Sign in is a
           * small daily annoyance for a rare convenience.
           */
          <button
            type="button"
            tabIndex={-1}
            aria-pressed={revealed}
            aria-label={revealed ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
            title={revealed ? 'Hide' : 'Show'}
            onClick={() => setRevealed((r) => !r)}
            className="absolute inset-y-0 right-0 grid w-11 place-items-center text-ink-3 hover:text-ink-2"
          >
            {revealed ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
          </button>
        )}
      </span>

      {hint && (
        // role="alert" only when it is a failure — announcing the standing
        // guidance on every keystroke would talk over everything else.
        <span
          role={invalid ? 'alert' : undefined}
          className={`mt-1.5 block text-[11.5px] ${invalid ? 'font-semibold text-crit' : 'text-ink-3'}`}
        >
          {hint}
        </span>
      )}
    </label>
  )
}

/**
 * Choosing a password, with the rules on screen before anything is typed.
 *
 * <b>Shared by accept-invite and reset</b> because it is the same act from the
 * person's side, and because these two screens were already written as copies
 * of each other — a rule fixed in one and missed in the other is the failure
 * this prevents (D-186).
 *
 * The requirements are listed rather than hidden behind a failed attempt.
 * "Choose a password" with no stated rule invites an eight-character one, and
 * being told afterwards is a wasted attempt on a screen most people see once.
 */
export function PasswordFields({
  password,
  confirm,
  onPassword,
  onConfirm,
  problem,
  showProblem,
}: {
  password: string
  confirm: string
  onPassword: (value: string) => void
  onConfirm: (value: string) => void
  problem: PasswordProblem | null
  /**
   * Whether to point at the offending box. False until a submit is attempted,
   * so an empty form is not red before anyone has typed in it.
   */
  showProblem: boolean
}) {
  const at = (field: PasswordProblem['field']) =>
    showProblem && problem?.field === field ? problem.message : undefined

  const longEnough = password.length >= MIN_PASSWORD_LENGTH
  const matches = confirm.length > 0 && password === confirm

  return (
    <>
      <Field
        label="New password"
        type="password"
        value={password}
        onChange={(e) => onPassword(e.target.value)}
        autoComplete="new-password"
        autoFocus
        hint={at('password')}
        invalid={at('password') !== undefined}
      />

      <Field
        label="Confirm password"
        type="password"
        value={confirm}
        onChange={(e) => onConfirm(e.target.value)}
        autoComplete="new-password"
        hint={at('confirm')}
        invalid={at('confirm') !== undefined}
      />

      <div className="mb-5 flex flex-col gap-1.5 rounded-lg bg-surface-2 px-3.5 py-3">
        <Requirement met={longEnough}>
          At least {MIN_PASSWORD_LENGTH} characters
          {/* The count only once there is something to count, so an untouched
              field does not read as a failure. */}
          {password.length > 0 && !longEnough && (
            <span className="text-ink-3 tabular"> &mdash; {password.length} so far</span>
          )}
        </Requirement>

        <Requirement met={matches}>Both entries match</Requirement>

        {/*
          Length is the whole rule (PasswordPolicy). NIST dropped the
          character-class requirements because they produce "Password1!", so
          saying "a short phrase" is the guidance that actually helps.
        */}
        <p className="mt-1 text-[11.5px] leading-relaxed text-ink-3">
          No capitals or symbols required. A short phrase you will remember beats a short jumble you
          will not.
        </p>
      </div>
    </>
  )
}

function Requirement({ met, children }: { met: boolean; children: ReactNode }) {
  return (
    <p className={`flex items-center gap-2 text-[12px] ${met ? 'font-semibold text-good' : 'text-ink-2'}`}>
      {met ? (
        <Check size={13} strokeWidth={3} className="shrink-0" />
      ) : (
        <Circle size={13} strokeWidth={2.5} className="shrink-0 text-ink-3" />
      )}
      <span>{children}</span>
    </p>
  )
}

export function SubmitButton({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="mt-1 w-full rounded-lg bg-accent px-4 py-2.5 text-[14px] font-semibold text-on-accent transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {busy ? 'Just a moment...' : children}
    </button>
  )
}
